// src/pages/About/QuemSomosPage.jsx

import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Button,
  Card,
  CardContent
} from '@mui/material';
import {
  School as SchoolIcon,
  Star as StarIcon,
  Group as GroupIcon,
  Gavel as GavelIcon,
  Security as SecurityIcon,
  Psychology as PsychologyIcon,
  Science as ScienceIcon,
  Insights as InsightsIcon,
  Verified as VerifiedIcon,
  Policy as PolicyIcon,
  Cloud as CloudIcon,
  Speed as SpeedIcon,
  Public as PublicIcon,
  Storage as StorageIcon,
  FindInPage as FindInPageIcon,
  BarChart as BarChartIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ManageSearch as ManageSearchIcon,
} from '@mui/icons-material';
import Footer from '../../components/Footer/Footer';

const QuemSomosPage = () => {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 8,
          mb: 6
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" gutterBottom align="center">
            Quem Somos
          </Typography>
          <Typography variant="h5" align="center" paragraph>
            Transformando inscrições e integridade académica em Moçambique
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button variant="contained" color="secondary" href="/register">
              Criar Conta
            </Button>
            <Button variant="outlined" color="inherit" href="/contact">
              Solicitar Demonstração
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Conteúdo Principal */}
      <Container maxWidth="lg" sx={{ flex: 1 }}>
        {/* Nossa História */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" gutterBottom>
            A Nossa História
          </Typography>
          <Typography paragraph>
            O ZenKroll, grupo da Data Base, Lda,  nasceu da necessidade de simplificar e dar confiança ao processo de
            inscrição e gestão académica nas instituições de ensino. A nossa jornada começou
            em 2022, com a missão de tornar as inscrições mais eficientes, transparentes e
            acessíveis para todos — e, desde então, evoluímos para um ecossistema que também
            garante a integridade académica nos Trabalhos de Conclusão de Curso (TCC).
          </Typography>
        </Box>

        {/* Números e Impacto */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <SchoolIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
                <Typography variant="h4" gutterBottom>
                  50+
                </Typography>
                <Typography variant="h6">Instituições Parceiras</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <GroupIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
                <Typography variant="h4" gutterBottom>
                  3,000+
                </Typography>
                <Typography variant="h6">Estudantes Atendidos</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <StarIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
                <Typography variant="h4" gutterBottom>
                  95%
                </Typography>
                <Typography variant="h6">Taxa de Satisfação</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Nossa Missão */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" gutterBottom>
            A Nossa Missão
          </Typography>
          <Typography paragraph>
            Facilitar o acesso à educação em Moçambique através de uma plataforma digital
            inovadora que conecta instituições e estudantes, tornando os processos académicos
            mais eficientes e transparentes, ao mesmo tempo que promove a integridade dos
            trabalhos científicos e TCCs.
          </Typography>
        </Box>

        {/* Nossos Valores */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" gutterBottom>
            Os Nossos Valores
          </Typography>
          <Grid container spacing={3}>
            {[
              {
                title: 'Inovação',
                description: 'Melhoria contínua com tecnologia a serviço da educação.',
              },
              {
                title: 'Transparência',
                description: 'Processos claros, registos auditáveis e comunicação aberta.',
              },
              {
                title: 'Compromisso',
                description: 'Dedicação total ao sucesso de instituições e estudantes.',
              }
            ].map((valor) => (
              <Grid item xs={12} md={4} key={valor.title}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    {valor.title}
                  </Typography>
                  <Typography>{valor.description}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Secção: TCC & Integridade Académica */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" gutterBottom>
            TCC & Integridade Académica
          </Typography>
          <Typography paragraph>
            O módulo de TCC do ZenKroll cobre todo o ciclo do trabalho final — desde a
            submissão da proposta até à defesa — integrando verificação de originalidade,
            detecção de uso de IA e trilhas de auditoria. O objetivo é garantir
            imparcialidade, rigor e conformidade com as normas académicas.
          </Typography>

          <Grid container spacing={3}>
            {[
              {
                icon: <ManageSearchIcon />,
                title: 'Submissão & Versionamento',
                text:
                  'Envio de proposta e monografia com controlo de versões, metadados e validações automáticas.'
              },
              {
                icon: <FindInPageIcon />,
                title: 'Revisão & Parecer',
                text:
                  'Fluxo de revisão por orientador, revisor e Juri, com pareceres e rubricas parametrizáveis.'
              },
              {
                icon: <GavelIcon />,
                title: 'Agendamento da Defesa',
                text:
                  'Agenda de defesas, formação de Juri, actas digitais, pontuações e emissão de credenciais.'
              },
              {
                icon: <BarChartIcon />,
                title: 'Relatórios & Métricas',
                text:
                  'Indicadores por curso/instituição: aprovação, reprovação, prazos, desempenho e conformidade.'
              }
            ].map((f) => (
              <Grid key={f.title} item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                    {f.icon}
                    <Typography variant="h6">{f.title}</Typography>
                  </Box>
                  <Typography>{f.text}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Secção: Tecnologias de Anti-Plágio e IA */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" gutterBottom>
            Tecnologias de Anti-Plágio e IA
          </Typography>
          <Typography paragraph>
            O ZenKroll combina técnicas de similaridade textual, pesquisa em repositórios,
            fingerprinting e padrões linguísticos para evidenciar semelhanças e possíveis
            marcas de geração por IA — sempre com relatórios claros e parâmetros ajustáveis.
          </Typography>

          <Grid container spacing={3}>
            {[
              {
                icon: <ScienceIcon />,
                title: 'Similaridade & Fontes',
                text:
                  'Comparação com bases institucionais, web pública e repositórios parceiros; percentuais por fonte.'
              },
              {
                icon: <PsychologyIcon />,
                title: 'Detecção de IA (ensemble)',
                text:
                  'Classificadores combinados + padrões linguísticos configuráveis por instituição e idioma.'
              },
              {
                icon: <InsightsIcon />,
                title: 'Evidências & Explicabilidade',
                text:
                  'Trechos destacados, pesos por evidência, histórico de versões e anexos do relatório técnico.'
              },
              {
                icon: <PolicyIcon />,
                title: 'Políticas & Limiares',
                text:
                  'Ajuste de limiares por curso/turma; políticas distintas para rascunhos, finais e reentregas.'
              }
            ].map((item) => (
              <Grid key={item.title} item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                    {item.icon}
                    <Typography variant="h6">{item.title}</Typography>
                  </Box>
                  <Typography>{item.text}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            {[
              {
                icon: <SecurityIcon />,
                title: 'Integridade & Auditoria',
                text:
                  'Trilhas de auditoria completas; perfis e permissões; registo de acções e carimbo temporal.'
              },
              {
                icon: <StorageIcon />,
                title: 'Dados & Privacidade',
                text:
                  'Armazenamento seguro; separação por instituição; exportação controlada de relatórios e metadados.'
              },
              {
                icon: <CloudIcon />,
                title: 'Escalabilidade',
                text:
                  'Processamento assíncrono de relatórios; filas de tarefas; cache de resultados frequentes.'
              },
              {
                icon: <SpeedIcon />,
                title: 'Performance',
                text:
                  'Índices dedicados por status/curso/período; normalização de entidades e consultas otimizadas.'
              }
            ].map((item) => (
              <Grid key={item.title} item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                    {item.icon}
                    <Typography variant="h6">{item.title}</Typography>
                  </Box>
                  <Typography>{item.text}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button variant="contained" color="primary" href="/integridade">
              Ver Relatórios de Integridade
            </Button>
            <Button variant="outlined" color="primary" href="/tcc">
              Explorar Módulo TCC
            </Button>
          </Box>
        </Box>

        {/* Fluxo do TCC (passo a passo) */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" gutterBottom>
            Como Funciona (Fluxo do TCC)
          </Typography>
          <Grid container spacing={3}>
            {[
              {
                step: '1. Proposta',
                icon: <PublicIcon />,
                text: 'Estudante submete a proposta; validações de requisitos e prevenção de duplicidade.',
              },
              {
                step: '2. Orientação',
                icon: <GroupIcon />,
                text: 'Atribuição de orientador; registo de encontros e objectivos por etapa.',
              },
              {
                step: '3. Similaridade & IA',
                icon: <FindInPageIcon />,
                text: 'Análise de similaridade e sinais de IA com evidências e parâmetros ajustáveis.',
              },
              {
                step: '4. Revisão',
                icon: <ManageSearchIcon />,
                text: 'Parecer do revisor/Juri; rubricas e comentários estruturados.',
              },
              {
                step: '5. Defesa',
                icon: <GavelIcon />,
                text: 'Agendamento, acta, notas; geração de relatório final e credenciais.',
              },
              {
                step: '6. Certificação',
                icon: <CheckCircleOutlineIcon />,
                text: 'Emissão de declaração/certidão conforme políticas da instituição.',
              }
            ].map((s) => (
              <Grid key={s.step} item xs={12} md={4}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {s.icon}
                    <Typography variant="h6">{s.step}</Typography>
                  </Box>
                  <Typography>{s.text}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Call to Action */}
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            bgcolor: 'grey.50',
            borderRadius: 2,
            mb: 6
          }}
        >
          <Typography variant="h4" gutterBottom>
            Faça Parte dessa Transformação
          </Typography>
          <Typography paragraph sx={{ mb: 4 }}>
            Junte-se a nós na missão de fortalecer a educação e a integridade académica.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button variant="contained" color="primary" size="large" href="/register">
              Comece Agora
            </Button>
            <Button variant="outlined" color="primary" size="large" href="/contact">
              Falar com a Equipa
            </Button>
          </Box>
        </Box>
      </Container>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default QuemSomosPage;
